<template>
    <div class="mx-auto max-w-[600px] px-3 pt-6 sm:px-6 sm:pt-12">
        <!-- Page Header -->
        <div class="flex items-center gap-2 mb-16">
            <CreditCard class="size-6 text-new-neutral-800" />
            <h1 class="text-3xl text-new-neutral-800">Billing</h1>
        </div>

        <!-- Tab Nav -->
        <BillingTabsV2 active="subscription" class="mb-8" />

        <!-- Content -->
        <div class="space-y-6 pb-12">
            <!-- Subscription Plans -->
            <PluginSubscriptionPlansCard
                :active-subscription-plans="activeSubscriptionPlans"
                :plugin-subscription="pluginSubscription"
                :should-allow-trial="shouldAllowTrial"
                @open-order-modal="openOrderModal"
                @open-switch-modal="(planUuid) => openSwitchDialog('switch', planUuid)"
                @open-cancel-modal="openSwitchDialog('cancel')"
            />

            <!-- Payment Method -->
            <PaymentMethodCard
                ref="paymentMethodCard"
                :billing-address="billingAddress"
                :countries="countries"
                @select-payment-method="selectedPaymentMethod = $event"
            />

            <!-- Billing Information -->
            <BillingInfoCard
                :billing-address="billingAddress"
                :countries="countries"
            />

            <!-- Invoice Emails -->
            <InvoiceEmailsCard
                v-model="invoiceEmails"
            />
        </div>

        <!-- Dialogs -->
        <SubscriptionOrderDialogV2
            v-if="orderModalPlanUuid"
            :open="orderModalOpen"
            :plan-uuid="orderModalPlanUuid"
            :subscription-plans="subscriptionPlans"
            :billing-address="billingAddress"
            :countries="countries"
            @close="closeOrderModal"
            @subscribed="closeOrderModal"
        />

        <PluginSubscriptionSwitchDialogV2
            v-if="pluginSubscription"
            :open="switchModalOpen"
            :plugin-subscription="pluginSubscription"
            :active-subscription-plans="activeSubscriptionPlans"
            :subscription-plans="subscriptionPlans"
            :initial-mode="switchModalMode"
            :initial-plan-uuid="switchModalInitialPlanUuid"
            @close="switchModalOpen = false"
            @canceled="openCancelReasonModal"
        />

        <SubscriptionCancelReasonDialogV2
            v-if="pluginSubscription"
            :open="cancelReasonModalOpen"
            :subscription-id="pluginSubscription.id"
            @close="cancelReasonModalOpen = false"
        />
    </div>
</template>

<script lang="ts" setup>
import { ref, PropType } from 'vue';
import { CreditCard } from 'lucide-vue-next';
import DefaultLayoutV2 from '@/Layouts/DefaultV2.vue';
import SubscriptionPlan from '@/types/Subscription/SubscriptionPlan';
import ISubscription from '@/types/Subscription/ISubscription';
import BillingAddress from '@/types/Subscription/BillingAddress';
import PluginSubscriptionPlansCard from '../components/PluginSubscriptionPlansCard.vue';
import PaymentMethodCard from '../components/PaymentMethodCard.vue';
import BillingInfoCard from '../components/BillingInfoCard.vue';
import InvoiceEmailsCard from '../components/InvoiceEmailsCard.vue';
import SubscriptionOrderDialogV2 from '../components/SubscriptionOrderDialogV2.vue';
import PluginSubscriptionSwitchDialogV2 from '../components/PluginSubscriptionSwitchDialogV2.vue';
import SubscriptionCancelReasonDialogV2 from '../components/SubscriptionCancelReasonDialogV2.vue';
import BillingTabsV2 from '../components/BillingTabsV2.vue';

defineOptions({
    layout: DefaultLayoutV2,
});

const props = defineProps({
    subscriptionPlans: {
        type: Array as PropType<SubscriptionPlan[]>,
        required: true,
    },
    activeSubscriptionPlans: {
        type: Array as PropType<SubscriptionPlan[]>,
        required: true,
    },
    billingAddress: {
        type: Object as PropType<BillingAddress>,
        required: true,
    },
    countries: {
        type: Array as PropType<Array<{ value: string; title: string }>>,
        required: true,
    },
    pluginSubscription: {
        type: Object as PropType<ISubscription | null>,
        default: null,
    },
    shouldAllowTrial: {
        type: Boolean,
        default: false,
    },
});

const selectedPaymentMethod = ref<unknown>(null);
const invoiceEmails = ref<string[]>([]);

// Order modal
const orderModalOpen = ref(false);
const orderModalPlanUuid = ref('');
const paymentMethodCard = ref<{ refresh: () => Promise<void> } | null>(null);

const openOrderModal = (planUuid: string) => {
    orderModalPlanUuid.value = planUuid;
    orderModalOpen.value = true;
};

const closeOrderModal = () => {
    orderModalOpen.value = false;
    orderModalPlanUuid.value = '';
    paymentMethodCard.value?.refresh();
};

// Switch modal
const switchModalOpen = ref(false);
const switchModalMode = ref<'switch' | 'cancel'>('switch');
const switchModalInitialPlanUuid = ref('');

const openSwitchDialog = (mode: 'switch' | 'cancel', planUuid: string = '') => {
    switchModalMode.value = mode;
    switchModalInitialPlanUuid.value = planUuid;
    switchModalOpen.value = true;
};

// Cancel reason modal
const cancelReasonModalOpen = ref(false);

const openCancelReasonModal = () => {
    switchModalOpen.value = false;
    cancelReasonModalOpen.value = true;
};
</script>
